/**
 * 맥스매치 운영 목록.
 * GET /admin/max-match/ops — 검색·상태 필터·결과 다운로드·신규 등록.
 */
import type { MaxMatchOpsListItem } from '@/entities/max-match/api/max-match-ops.api';
import {
  useDeleteMaxMatchOpsSummary,
  useDownloadMaxMatchOpsExcel,
  useMaxMatchOpsList,
} from '@/entities/max-match/hooks/useMaxMatchOps';
import { MaxMatchOpsCreateDialog } from '@/pages/max-match/MaxMatchOpsCreateDialog';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/shared/components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { FilterRow } from '@/shared/components/ui/row';
import {
  maxMatchOpsDetailPath,
} from '@/shared/constants/routes';
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import {
  downloadXlsx,
  MAX_MATCH_OPS_EXCEL_HEADER_MAP,
} from '@/shared/utils/xlsxExport';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { formatDateTime } from '@/shared/lib/dateFormat';
import { useNavigate } from '@tanstack/react-router';
import {
  Download,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

const STATUS_ALL = 'ALL';

const STATUS_FILTER_OPTIONS = [
  { value: STATUS_ALL, label: '전체' },
  { value: 'BEFORE_MATCH', label: '매칭 전' },
  { value: 'MATCHED', label: '매칭 완료' },
] as const;

const statusLabel = (status: string): string => {
  switch (status) {
    case 'BEFORE_MATCH':
      return '매칭 전';
    case 'MATCHED':
      return '매칭 완료';
    default:
      return status || '-';
  }
};

const statusBadgeVariant = (
  status: string,
): 'warning' | 'success' | 'secondary' => {
  switch (status) {
    case 'BEFORE_MATCH':
      return 'warning';
    case 'MATCHED':
      return 'success';
    default:
      return 'secondary';
  }
};

/** 미매칭 등 null 값은 `-`로 표시 */
const formatMatchValue = (
  value: number | string | null | undefined,
  format: 'count' | 'amount' = 'count',
): string => {
  if (value == null || value === '') return '-';
  if (format === 'amount' && typeof value === 'number') {
    return value.toLocaleString('ko-KR');
  }
  return String(value);
};

const SEARCH_DEFAULTS = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  searchKeyword: '',
  status: STATUS_ALL,
};

export const MaxMatchOpsPage: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const { values, setValues } = useListSearchParams(SEARCH_DEFAULTS);

  const [keyword, setKeyword] = useState(values.searchKeyword);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    setKeyword(values.searchKeyword);
  }, [values.searchKeyword]);

  const search = React.useMemo(
    () => ({
      searchKeyword: values.searchKeyword || undefined,
      status:
        values.status && values.status !== STATUS_ALL
          ? (values.status as 'BEFORE_MATCH' | 'MATCHED')
          : undefined,
    }),
    [values.searchKeyword, values.status],
  );

  const pageable = React.useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error } = useMaxMatchOpsList(
    search,
    pageable,
  );
  const downloadExcel = useDownloadMaxMatchOpsExcel();
  const deleteSummary = useDeleteMaxMatchOpsSummary();

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      searchKeyword: keyword.trim(),
      page: 1,
    });
  };

  /** 매칭 여부 라디오 변경 시 즉시 목록 재조회 */
  const handleStatusChange = (next: string) => {
    setValues({
      status: next || STATUS_ALL,
      page: 1,
    });
  };

  const goDetail = (row: MaxMatchOpsListItem) => {
    navigate({
      to: maxMatchOpsDetailPath(row.summaryId),
    } as never);
  };

  const handleDownload = async (summaryId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await downloadExcel.mutateAsync(summaryId);
      downloadXlsx(
        result,
        `맥스매치_운영_${summaryId}`,
        MAX_MATCH_OPS_EXCEL_HEADER_MAP,
      );
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleDelete = async (row: MaxMatchOpsListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await confirm({
      title: '운영 건 삭제',
      description:
        '운영 건과 하위 이미지·처방 내역·결과·S3 파일이 모두 삭제되며 복구할 수 없습니다. 삭제하시겠습니까?',
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteSummary.mutateAsync(row.summaryId);
      toast.success('운영 건이 삭제되었습니다.');
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="맥스매치 운영"
        description="어드민 맥스매치 운영 건 등록·검수·매칭"
        actions={
          <MenuWriteGate>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              신규 등록
            </Button>
          </MenuWriteGate>
        }
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <Input
            className="max-w-xs"
            placeholder="제공업체명 또는 병의원명"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Button type="submit">
            <Search className="mr-2 h-4 w-4" />
            검색
          </Button>
        </FilterRow>
        <div className="space-y-2">
          <Label>매칭 여부</Label>
          <RadioGroup
            value={values.status}
            onValueChange={handleStatusChange}
            className="flex flex-wrap gap-4"
            aria-label="매칭 여부"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                htmlFor={`ops-status-${opt.value}`}
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <RadioGroupItem
                  value={opt.value}
                  id={`ops-status-${opt.value}`}
                />
                {opt.label}
              </label>
            ))}
          </RadioGroup>
        </div>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="목록을 불러오는 중..." />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState description="등록된 운영 건이 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>생성계정</TableHead>
                  <TableHead>제공업체</TableHead>
                  <TableHead>병의원명</TableHead>
                  <TableHead>등록일시</TableHead>
                  <TableHead className="text-right">처방 데이터</TableHead>
                  <TableHead className="text-right">유지</TableHead>
                  <TableHead className="text-right">변경</TableHead>
                  <TableHead className="text-right">예상금액</TableHead>
                  <TableHead className="text-right">매칭금액</TableHead>
                  <TableHead>결과</TableHead>
                  <TableHead className="w-36">액션</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const matched = row.status === 'MATCHED';
                  return (
                    <TableRow
                      key={row.summaryId}
                      className="cursor-pointer"
                      onClick={() => goDetail(row)}
                    >
                      <TableCell>
                        {formatMatchValue(row.createdAdminEmail)}
                      </TableCell>
                      <TableCell>
                        {formatMatchValue(row.pharmaCompanyName)}
                      </TableCell>
                      <TableCell>{formatMatchValue(row.clinicName)}</TableCell>
                      <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        {formatMatchValue(row.imageCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatMatchValue(row.unchangedCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatMatchValue(row.changedCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatMatchValue(
                          row.originEstimatedTotalAmount,
                          'amount',
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatMatchValue(
                          row.newEstimatedTotalAmount,
                          'amount',
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(row.status)}>
                          {statusLabel(row.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!matched || downloadExcel.isPending}
                            onClick={(e) =>
                              void handleDownload(row.summaryId, e)
                            }
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <MenuWriteGate>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={deleteSummary.isPending}
                              onClick={(e) => void handleDelete(row, e)}
                              aria-label="운영 건 삭제"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </MenuWriteGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) =>
                setValues({ page: 1, pageSize })
              }
            />
          </>
        )}
      </DataCard>

      <MaxMatchOpsCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(summaryId) => {
          navigate({
            to: maxMatchOpsDetailPath(summaryId),
          } as never);
        }}
      />
    </div>
  );
};
